// Notifications : activer le push sur cet appareil, puis la liste (non lues
// en premier plan). Toucher une notification la marque lue et ouvre sa fiche.
// Les mots de chaque rôle (D-103) : un joueur, un parent, un coach lisent « convocations, renforts » ;
// un bénévole « créneaux, rappels la veille, demandes du bureau » ; un partenaire, au vouvoiement,
// « appels du club, réponses à vos demandes ». Les mêmes mots dans Moi et dans l'aide.
// Une notification sans écran à elle (« #/ ») : l'écran et la casquette d'après sa clé (src/domain/notif-links.ts).
import { disablePush, enablePush, listNotifications, markRead, pushState, pushSupport, type Notif } from '../lib/notifications';
import { localDate } from '../domain/schedule';
import { notifLink } from '../domain/notif-links';
import { hatAmong, modOn, personaOf, switchHat, type Ctx } from './context';
import { $, $$, html, mount, toast } from './dom';
import { vouvoie } from './vous';
import { dayScores } from '../domain/follow';
import './desist.css';

/** Pour qui sont les mots des notifications : un membre (joueur, parent, coach, bureau), un bénévole, un partenaire. */
export type NotifRole = 'membre' | 'benevole' | 'partenaire' | 'abonne';
export const notifRole = (c: Ctx | undefined): NotifRole =>
  !c ? 'membre' : vouvoie(c) ? 'partenaire' : personaOf(c) === 'supporter' ? 'benevole' : personaOf(c) === 'abonne' ? 'abonne' : 'membre';

/** Les mots de chaque rôle : la carte du téléphone, la page vide, la ligne de Moi, l'intro de l'aide. */
export const NOTIF_WORDS: Record<NotifRole, { lead: string; what: string; row: string; empty: string; intro: string }> = {
  membre: {
    lead: 'Ne rate plus une convocation.', what: 'Rappel la veille des séances, renforts, annulations : directement sur cet appareil.',
    row: 'Sur ce téléphone : convocations, rappels, annonces', empty: 'Convocations, annulations et rappels arriveront ici.',
    intro: 'Convocations, annulations, rappels la veille, annonces : directement sur le téléphone.',
  },
  benevole: {
    lead: 'Ne rate plus un créneau.', what: 'Tes créneaux, les rappels la veille, les demandes du bureau : directement sur cet appareil.',
    row: 'Sur ce téléphone : créneaux, rappels la veille, demandes du bureau', empty: 'Tes créneaux, les rappels la veille et les demandes du bureau arriveront ici.',
    intro: 'Créneaux, rappels la veille, demandes du bureau : directement sur le téléphone.',
  },
  abonne: {
    lead: 'Le score arrive dès la fin du match.', what: 'Les résultats et les changements des équipes que tu suis : directement sur cet appareil.',
    row: 'Sur ce téléphone : résultats, matchs déplacés', empty: 'Les résultats et les changements des équipes que tu suis arriveront ici.',
    intro: 'Les résultats et les changements des équipes suivies : directement sur le téléphone.',
  },
  partenaire: {
    lead: 'Ne manquez plus un appel du club.', what: 'Les appels du club, les réponses à vos demandes : directement sur cet appareil.',
    row: 'Sur ce téléphone : appels du club, réponses à vos demandes', empty: 'Les appels du club et les réponses à vos demandes arriveront ici.',
    intro: 'Appels du club, réponses à vos demandes : directement sur le téléphone.',
  },
};

const ICON: Record<string, string> = {
  invite: '🏀', cancelled: '✕', scheduled: '＋', result: '🏆', absence: '–', unavailability: '⏸', reminder: '⏰', announcement: '📣', trial: '👋', partner: '🤝', ffbb: '📅', document: '📄', derogation: '🔁', volunteer: '🙌', cheer: '📣', support: '💬', grant: '🗂', hall: '🏟',
  receipt: '🧾', bureau: '✉',   // reçus fiscaux (D-124), messages au bureau (D-126)
  feeling: '🙂', review: '📋',   // ressentis, bilan de saison (D-143)
  late: '–', checkin: '💬',   // prévenu tard, « Tout va bien ? » (D-148)
  skin: '🎨',   // un nouveau skin (D-157 : dans la cloche, jamais sur le téléphone)
  challenge: '🏅',   // le résumé du jour des défis au coach (D-160 : dans la cloche, jamais sur le téléphone)
  game: '🎯',   // Mains vives proposé par le coach (D-164)
  accord: '🛡', carpool: '🚗',   // les accords des parents, une place proposée (D-169)
  shop: '🛍',   // la boutique du club (D-181)
};
/** « Sarah ne vient pas ce soir · prévenue tard » : la fin en pastille (D-148, maquette d1), accordée. */
const LATE_TAG = / · (prévenue? tard)$/;

function when(iso: string): string {
  const d = new Date(iso);
  const day = localDate(d) === localDate(new Date()) ? '' : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) + ' · ';
  return day + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }).replace(':', 'h');
}

export async function renderNotifs(root: HTMLElement, onChange: () => void, ctx?: Ctx): Promise<void> {
  const [list, state] = await Promise.all([listNotifications(), pushState()]);
  const support = pushSupport();
  const unread = list.filter(n => !n.read_at);
  const role = notifRole(ctx), w = NOTIF_WORDS[role], vous = role === 'partenaire';

  const pushCard = support === 'ok' ? html`
    <section class="block push-card ${state === 'on' ? 'on' : ''}" data-guide="notif-card">
      ${state === 'on' ? html`
        <div class="push-row"><span class="dot-on" aria-hidden="true"></span><p>Notifications activées sur cet appareil.</p></div>
        <button type="button" class="btn btn-link btn-sm" id="b-push-off">Désactiver ici</button>`
      : state === 'denied' ? html`
        <p><strong>Notifications bloquées</strong> dans les réglages du navigateur pour cette app. ${vous ? 'Autorisez-les puis revenez ici.' : 'Autorise-les puis reviens ici.'}</p>`
      : html`
        <p><strong>${w.lead}</strong> ${w.what}</p>
        <button type="button" class="btn btn-primary" id="b-push-on" data-guide="notif-on">Activer les notifications</button>`}
    </section>`
    : support === 'ios-install' ? html`
    <section class="block push-card" data-guide="notif-card">
      <p><strong>Sur iPhone :</strong> ${vous ? 'ajoutez d\'abord Arceau à l\'écran d\'accueil (bouton Partager → « Sur l\'écran d\'accueil »), ouvrez-la depuis l\'icône, puis revenez ici pour activer les notifications.'
        : 'ajoute d\'abord Arceau à l\'écran d\'accueil (bouton Partager → « Sur l\'écran d\'accueil »), ouvre-la depuis l\'icône, puis reviens ici pour activer les notifications.'}</p>
    </section>` : '';

  const item = (n: Notif) => html`
    <li>
      <button type="button" class="nt ${n.read_at ? '' : 'nt-new'}" data-id="${n.id}" data-url="${n.url ?? ''}">
        <span class="nt-ico nt-${n.kind}" aria-hidden="true">${n.kind === 'scheduled' && /^Match déplacé/.test(n.title) ? '📅' : ICON[n.kind] ?? '•'}</span>
        <span class="nt-main">
          <span class="nt-title">${n.kind === 'late' && LATE_TAG.test(n.title) ? html`${n.title.replace(LATE_TAG, '')} · <span class="ds-late">${LATE_TAG.exec(n.title)![1]}</span>` : n.title}</span>
          ${n.body ? html`<span class="nt-body">${n.kind === 'result' && n.body.includes(' · ') && n.body.includes('✓')
            ? html`<span class="fw-scores">${dayScores(n.body).map((x, i) => html`${i ? ' · ' : ''}${x.win ? html`<b>${x.text}</b>` : html`<span>${x.text}</span>`}`)}</span>` : n.body}</span>` : ''}
          <span class="nt-when">${when(n.created_at)}</span>
        </span>
      </button>
    </li>`;

  mount(root, html`
    <section class="screen">
      <header class="screen-head head-row">
        <div><p class="eyebrow">${unread.length ? `${unread.length} non lue${unread.length > 1 ? 's' : ''}` : 'Tout est lu'}</p><h2>Notifications</h2></div>
        ${unread.length ? html`<button type="button" class="btn btn-ghost btn-sm" id="b-all">Tout marquer lu</button>` : ''}
      </header>
      ${pushCard}
      ${list.length ? html`<ul class="nt-list">${list.map(item)}</ul>` : html`
        <div class="empty"><p class="empty-title">Rien pour l'instant.</p><p>${w.empty}</p></div>`}
    </section>`);

  const reload = async () => { onChange(); await renderNotifs(root, onChange, ctx); };
  $(root, '#b-push-on')?.addEventListener('click', async () => {
    const r = await enablePush();
    toast(r === 'on' ? 'Notifications activées' : r === 'denied' ? 'Autorisation refusée' : 'Activation impossible', r === 'on' ? 'ok' : 'ko');
    await reload();
  });
  $(root, '#b-push-off')?.addEventListener('click', async () => { await disablePush(); toast('Désactivées sur cet appareil'); await reload(); });
  $(root, '#b-all')?.addEventListener('click', async () => { await markRead(unread.map(n => n.id)); await reload(); });
  for (const b of $$<HTMLButtonElement>(root, '.nt')) {
    b.addEventListener('click', async () => {
      await markRead([b.dataset.id!]);
      onChange();
      const n = list.find(x => x.id === b.dataset.id);
      const c = ctx;
      const go = n ? notifLink(n, { self: c ? c.full.people.find(p => p.user_id === c.uid)?.id ?? null : null, on: k => !c || modOn(c, k) }) : null;
      if (go?.hat === 'coach' && ctx) {
        // La carte est sur l'accueil du coach : on y passe (le petit bandeau « Côté Coach » le dit, D-101).
        const hat = hatAmong(ctx, ['coach']);
        if (hat) switchHat(ctx, hat);
        if (hat || ctx.coachOf.size) { location.hash = '#/'; return; }
      }
      if (go && (go.hash !== '#/' || go.home)) location.hash = go.hash; else await renderNotifs(root, onChange, ctx);
    });
  }
}
